// CARO. Casa de estilo — the image-consulting services (/caro/servicios).
//
// The "ángulos de la asesoría" live as services inside two sub-tabs
// (subpestañas): personal work vs. brands/stores/groups. Each service maps to
// the `services.svc.<key>.*` dictionary entries. `bodies` lists the paragraphs
// that exist; `listAfter` (with optional `listTitle`) inserts the bullet list
// after that paragraph; `meta`/`note` flag the extra lines. `priceFrom` is the
// starting price in EUR of the services whose `price` text carries a number
// (schema.org Offer minPrice and priceRange; tests/jsonld.test.ts checks every
// locale's text contains it): change both together.
// Shared by ServicesPage (client) and the Worker's prerendered #root.

export interface ServiceDef {
  key: string;
  bodies: number[];
  meta?: boolean;
  note?: boolean;
  listAfter?: number;
  listTitle?: boolean;
  /** Starting price in EUR, as written in `services.svc.<key>.price` (none = by format / quote). */
  priceFrom?: number;
}

export const SERVICE_GROUPS: { key: 'personal' | 'pro'; services: ServiceDef[] }[] = [
  {
    key: 'personal',
    services: [
      { key: 'sesion', bodies: [1, 2, 3], meta: true, note: true, listAfter: 1, listTitle: true, priceFrom: 300 },
      { key: 'esencial', bodies: [1, 2], meta: true, note: true, priceFrom: 120 },
      { key: 'compras', bodies: [1, 2, 3], priceFrom: 180 },
    ],
  },
  {
    key: 'pro',
    services: [
      { key: 'tienda', bodies: [1, 2, 3], meta: true, note: true, priceFrom: 300 },
      { key: 'privadas', bodies: [1, 2], note: true },
      { key: 'medida', bodies: [1, 2], listAfter: 1 },
    ],
  },
];

/** A service's bullet list (`services.svc.<key>.list`, one item per line). */
export function serviceListItems(list: string): string[] {
  return list.split('\n').map((s) => s.trim()).filter(Boolean);
}

/**
 * One page per service (docs/plan-tests-y-servicios.md): /caro/servicios/<slug>, slugs translated per
 * language (the Spanish one is the logical slug). The tests linked from each page (keys of
 * lib/colorimetry/landing GameId; none = the tests hub).
 */
export const SERVICES_PATH = '/caro/servicios';

export const SERVICE_PAGES: Readonly<Record<string, { slugs: { es: string; en: string; fr: string }; tests: ('colorimetry' | 'silhouette' | 'face')[] }>> = {
  sesion: { slugs: { es: 'sesion-caro', en: 'caro-session', fr: 'seance-caro' }, tests: ['colorimetry', 'silhouette', 'face'] },
  esencial: { slugs: { es: 'sesion-esencial', en: 'essential-session', fr: 'seance-essentielle' }, tests: ['colorimetry', 'face'] },
  compras: { slugs: { es: 'personal-shopper', en: 'personal-shopper', fr: 'personal-shopper' }, tests: ['silhouette', 'colorimetry'] },
  tienda: { slugs: { es: 'experiencia-en-tienda', en: 'in-store-experience', fr: 'experience-en-boutique' }, tests: ['colorimetry'] },
  privadas: { slugs: { es: 'experiencias-privadas', en: 'private-experiences', fr: 'experiences-privees' }, tests: ['colorimetry'] },
  medida: { slugs: { es: 'a-medida', en: 'bespoke', fr: 'sur-mesure' }, tests: [] },
};

/** The tests a service page links to: its own, or all three when it has none. */
export const serviceTests = (key: string): ('colorimetry' | 'silhouette' | 'face')[] => {
  const own = SERVICE_PAGES[key]?.tests ?? [];
  return own.length ? own : ['colorimetry', 'silhouette', 'face'];
};

/** The logical path of a service page (add the language with localizedPath). */
export const servicePath = (key: string): string => `${SERVICES_PATH}/${SERVICE_PAGES[key]!.slugs.es}`;

/** The service behind a logical (Spanish) slug, or undefined. */
export const serviceKeyBySlug = (esSlug: string): string | undefined =>
  Object.entries(SERVICE_PAGES).find(([, p]) => p.slugs.es === esSlug)?.[0];

/** Public slug in `locale` of a Spanish slug (unknown: unchanged). */
export const serviceSlugFor = (locale: 'es' | 'en' | 'fr', esSlug: string): string =>
  Object.values(SERVICE_PAGES).find((p) => p.slugs.es === esSlug)?.slugs[locale] ?? esSlug;

/** Spanish slug of a public slug: the locale's own wins, any language's is accepted (old or mixed URLs). */
export function serviceEsSlug(locale: 'es' | 'en' | 'fr', slug: string): string {
  const all = Object.values(SERVICE_PAGES).map((p) => p.slugs);
  return (all.find((s) => s[locale] === slug) ?? all.find((s) => s.es === slug || s.en === slug || s.fr === slug))?.es ?? slug;
}
